import { type FormEvent, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import {
  type WatchlistNameValues,
  watchlistNameSchema,
} from "./watchlistName.schema";

export type WatchlistNameDialogMode = "create" | "rename";

export interface UseWatchlistNameDialogControllerInput {
  mode: WatchlistNameDialogMode;
  initialName: string;
  onSubmit: (name: string) => void;
}

export interface WatchlistNameDialogControllerOutput {
  name: string;
  validationError: string | null;
  onNameChange: (value: string) => void;
  onSubmit: (event?: FormEvent<HTMLFormElement>) => Promise<void>;
}

export const useWatchlistNameDialogController = ({
  mode,
  initialName,
  onSubmit,
}: UseWatchlistNameDialogControllerInput): WatchlistNameDialogControllerOutput => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<WatchlistNameValues>({
    resolver: zodResolver(watchlistNameSchema),
    defaultValues: { name: initialName },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: mode is a trigger, not a read — switching create/rename on the same initialName still means a fresh draft.
  useEffect(() => {
    reset({ name: initialName });
  }, [mode, initialName, reset]);

  const name = watch("name");
  const validationError = errors.name?.message ?? null;
  const onNameChange = (value: string) => setValue("name", value);
  const submit = handleSubmit((values) => {
    onSubmit(values.name);
  });

  return {
    name: name ?? "",
    validationError,
    onNameChange,
    onSubmit: submit,
  };
};
